import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import EllipsisH from '@decisiv/iconix/lib/components/EllipsisH';
import UserPlus from '@decisiv/iconix/lib/components/UserPlus';
import UserTimes from '@decisiv/iconix/lib/components/UserTimes';
import User from '@decisiv/iconix/lib/components/User';
import Popover from '@decisiv/ui-components/lib/components/Popover';

import { editUserPath } from '../../shared/paths';
import { ROW_ACTIONS } from './accessCopy';
import { MenuItem, MenuList, MenuTrigger } from './styles';

/**
 * The ACTIONS cell's kebab menu (PD-4677): View Details, plus the one access
 * action that applies to the tab the row is on.
 *
 * A Popover of plain buttons rather than a KeyDS Menu, matching the shared
 * OverflowPopover the header tags use — KeyDS has no menu component, and the
 * design's panel is a list of links with a divider.
 *
 * Closes on choosing an item so the modal does not open behind an open panel.
 */
function RowActionsMenu({ isRemoved, onRemove, onRestore, user }) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);

  const choose = (act) => () => {
    setIsOpen(false);
    act();
  };

  return (
    <Popover
      hideOnClickOutside
      onRequestHide={() => setIsOpen(false)}
      placement="bottom-end"
      showArrow={false}
      target={
        <MenuTrigger
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label={ROW_ACTIONS.toggle(user.name)}
          onClick={() => setIsOpen((open) => !open)}
          ref={triggerRef}
          type="button"
        >
          <EllipsisH />
        </MenuTrigger>
      }
      visible={isOpen}
    >
      <MenuList role="menu">
        <MenuItem
          as="a"
          href={editUserPath(user.id)}
          role="menuitem"
          onClick={() => setIsOpen(false)}
        >
          <User size="small" />
          {ROW_ACTIONS.viewDetails}
        </MenuItem>

        {isRemoved ? (
          <MenuItem
            onClick={choose(() => onRestore(user))}
            role="menuitem"
            type="button"
          >
            <UserPlus size="small" />
            {ROW_ACTIONS.restore}
          </MenuItem>
        ) : (
          <MenuItem
            onClick={choose(() => onRemove(user))}
            role="menuitem"
            type="button"
          >
            <UserTimes size="small" />
            {ROW_ACTIONS.remove}
          </MenuItem>
        )}
      </MenuList>
    </Popover>
  );
}

RowActionsMenu.propTypes = {
  /** Which tab the row is on, and so which access action it offers. */
  isRemoved: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
  }).isRequired,
};

RowActionsMenu.defaultProps = {
  isRemoved: false,
};

export default RowActionsMenu;
